import React from 'react';
import TopbarNav from 'components/navigational-component/topbar-nav/TopbarNav';
import CustomizedDataGrid from 'components/wrapped-component/customized-data-grid/CustomizedDataGrid';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import toast from 'CustomToast';
import { httpWaferControlMap } from 'services/http.wafer-control-map';
import Button from '../wrapped-component/hint-controls/Button';
import ModalPopup from 'components/wrapped-component/modal-popup/modal-popup';
import { parseWaferMapFile, WaferMapParserResult, DieType } from './wafer-map-file-parser';
import DieTypeMappingPopup from './die-type-mapping/die-type-mapping-popup';

interface WaferControlMapsProps extends RouteComponentProps {
  className?: string
}

interface WaferControlMapsState {
  waferControlMaps: any[];
  isUploading: boolean;
  parsedFileData: WaferMapParserResult | null;
  showDieTypeMappingPopup: boolean;
}

class WaferControlMaps extends React.Component<WaferControlMapsProps, WaferControlMapsState> {
  private fileInputRef: React.RefObject<HTMLInputElement>;

  constructor(props: any) {
    super(props);
    this.state = {
      waferControlMaps: [],
      isUploading: false,
      parsedFileData: null,
      showDieTypeMappingPopup: false,
    };
    this.fileInputRef = React.createRef<HTMLInputElement>();
  }

  componentDidMount() {
    this.getWaferControlMaps();
  }

  isValidDateValue = (value: any) => {
    if (!value) return false;
    const date = new Date(value);
    return !Number.isNaN(date.getTime()) && date.getFullYear() > 1;
  };

  formatDateTime = (value: any) => {
    if (!this.isValidDateValue(value)) return '';

    const date = new Date(value);
    const pad = (datePart: number) => datePart.toString().padStart(2, '0');
    const hours = date.getHours();
    const displayHours = hours % 12 || 12;
    const amPm = hours >= 12 ? 'PM' : 'AM';
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${displayHours}:${pad(date.getMinutes())}:${pad(date.getSeconds())} ${amPm}`;
  };

  normalizeWaferControlMaps = (waferControlMaps: any[]) => {
    if (!Array.isArray(waferControlMaps)) return [];
    return waferControlMaps.map((wcm) => {
      return {
        ...wcm,
        createdOn: this.formatDateTime(wcm.createdOn),
        updatedOn: this.formatDateTime(wcm.updatedOn),
      };
    });
  };

  getWaferControlMaps = async () => {
    const wcmDataResponse = await httpWaferControlMap.getWaferControlMaps();
    if (wcmDataResponse) {
      if (wcmDataResponse.success) {
        this.setState({ waferControlMaps: this.normalizeWaferControlMaps(wcmDataResponse.data) });
      } else if (wcmDataResponse.message) {
        toast.error(wcmDataResponse.message);
      }
    }
  };

  onRowRemoved = async (event: any) => {
    // Older deletion path removed. Deletions are handled in `onRowRemoving`.
    return;
  };

  onRowRemoving = (event: any) => {
    // Always cancel the grid's built-in removal immediately so we control deletion flow.
    // eslint-disable-next-line no-param-reassign
    event.cancel = true;
    const name = event.data && event.data.name ? event.data.name : 'this wafer map';
    (async () => {
       try {
      const confirmed = await ModalPopup.confirm({
        header: 'Confirmation',
        body: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
        yesButtonText: 'Delete',
        noButtonText: 'Cancel',
        size: 'sm',
      });
      if (!confirmed) return;
      const response = await httpWaferControlMap.deleteWaferControlMap(event.data.id);
      if (response) {
        if (response.success) {
          toast.success('Wafer control map deleted successfully');
          this.setState({ waferControlMaps: this.normalizeWaferControlMaps(response.data) });
        } else if (response.message) {
          toast.error(response.message);
        }
        }
        } catch (error) {
        console.error('Error deleting wafer control map:', error);
        toast.error('An error occurred while deleting the wafer control map.');
      }
    })();
  };

  handleCreateFromFileClick = () => {
    this.fileInputRef.current?.click();
  };

  handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    // Validate file extension
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.txt')) {
      toast.error('Please select a .txt file only');
      // Reset the input
      if (this.fileInputRef.current) {
        this.fileInputRef.current.value = '';
      }
      return;
    }

    // Parse the file
    this.setState({ isUploading: true });
    
    try {
      // Read file content
      const fileContent = await this.readFileContent(file);
      
      // Parse the wafer map file
      const parsedData = parseWaferMapFile(fileContent);
      
      // Validate parsed data - check if it has required wafer data
      if (!this.isValidWaferMapData(parsedData)) {
        toast.error('Please upload a valid file. The file must contain wafer data with rows, columns, and character mappings.');
        // Reset the input
        if (this.fileInputRef.current) {
          this.fileInputRef.current.value = '';
        }
        return;
      }
      
      // Store parsed data in state
      this.setState({ 
        parsedFileData: parsedData,
        showDieTypeMappingPopup: true, // Show popup after parsing
      });
      
    } catch (error: any) {
      console.error('Error parsing file:', error);
      toast.error('An error occurred while parsing the file. Please check the file format and try again.');
    } finally {
      this.setState({ isUploading: false });
      // Reset the input
      if (this.fileInputRef.current) {
        this.fileInputRef.current.value = '';
      }
    }
  };

  readFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(event.target.result as string);
        } else {
          reject(new Error('Failed to read file content'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      reader.readAsText(file);
    });
  };

  // Validate that parsed data contains required wafer map information
  isValidWaferMapData = (parsedData: WaferMapParserResult | null): boolean => {
    if (!parsedData) {
      return false;
    }

    // Check if metadata exists and has required fields
    const { metadata, allCharacters } = parsedData;
    
    if (!metadata) {
      return false;
    }

    // Check for required metadata fields
    if (!metadata.rows || !metadata.columns || metadata.rows <= 0 || metadata.columns <= 0) {
      return false;
    }

    // Check if allCharacters array exists and has data
    if (!allCharacters || !Array.isArray(allCharacters) || allCharacters.length === 0) {
      return false;
    }

    // Check if at least some characters have valid coordinates
    const hasValidCharacters = allCharacters.some((charData: any) => {
      const x = parseInt(charData.x, 10);
      const y = parseInt(charData.y, 10);
      return !isNaN(x) && !isNaN(y) && x >= 0 && y >= 0;
    });

    if (!hasValidCharacters) {
      return false;
    }

    return true;
  };

  handleDieTypeMappingClose = () => {
    this.setState({ showDieTypeMappingPopup: false });
  };

  render() {
    const { className, history } = this.props;
    const { waferControlMaps, isUploading, parsedFileData, showDieTypeMappingPopup } = this.state;
    return (
      <div
        className={`wcm-full-height-container wcm-root ${className !== '' ? className : ''}`}
      >
        <input
          ref={this.fileInputRef}
          type="file"
          accept=".txt"
          className="wcm-hidden-file-input"
          onChange={this.handleFileChange}
          disabled={isUploading}
        />
        <TopbarNav
          title="Wafer Control Maps"
          items={[]}
          showAvatar
          showNotifications
          secondaryActions={(
            <>
              <Button
                onClick={this.handleCreateFromFileClick}
                size="sm"
                variant="primary"
                className="mr20 mt-5"
                disabled={isUploading}
              >
                {isUploading ? 'Parsing...' : 'Create from File'}
              </Button>
              <Button
                onClick={() => {
                  history.push('/wafer-control-map');
                }}
                size="sm"
                variant="primary"
                className="mr20 mt-5"
              >
                Create Wafer Control Map
              </Button>
            </>
          )}
        />
        <div className="wcm-content-area">
          <div className="wcm-content-column">
            <CustomizedDataGrid
              height="calc(100vh - 120px)"
              totalRowsToDisplay={100}
              showAdvancedFilters={false}
              showFilterRow
              allowDeleting
              showContextMenu={false}
              allowUpdating
              onRowRemoving={this.onRowRemoving}
              onRowRemoved={this.onRowRemoved}
              onEditingStart={(event: any) => {
                // eslint-disable-next-line no-param-reassign
                event.cancel = true;
                history.push(`/wafer-control-map/${event.data.id}`);
              }}
              fields={[
                {
                  caption: 'Name',
                  dataField: 'name',
                  dataType: 'string',
                  showInfo: false,
                  minWidth: 200,
                  wordWrap: true,
                },
                {
                  caption: 'Owner',
                  dataField: 'owner',
                  dataType: 'string',
                  showInfo: false,
                  minWidth: 200,
                  wordWrap: true,
                },
                {
                  caption: 'Version',
                  dataField: 'version',
                  dataType: 'number',
                  showInfo: false,
                  width: 100,
                },
                {
                  caption: 'State',
                  dataField: 'state',
                  dataType: 'string',
                  showInfo: false,
                  width: 100,
                },
                {
                  caption: 'Access',
                  dataField: 'access',
                  dataType: 'string',
                  showInfo: false,
                  width: 100,
                },
                {
                  caption: 'Description',
                  dataField: 'description',
                  dataType: 'string',
                  showInfo: false,
                  minWidth: 200,
                },
                {
                  caption: 'Created On',
                  dataField: 'createdOn',
                  dataType: 'string',
                  showInfo: false,
                  width: 170,
                },
                {
                  caption: 'Updated On',
                  dataField: 'updatedOn',
                  dataType: 'string',
                  showInfo: false,
                  width: 170,
                },
              ]}
              tableData={waferControlMaps}
            />
          </div>
        </div>
        {parsedFileData && (
          <DieTypeMappingPopup
            show={showDieTypeMappingPopup}
            uniqueCharacters={parsedFileData.uniqueCharacters}
            parsedFileData={parsedFileData}
            onClose={this.handleDieTypeMappingClose}
            history={this.props.history}
          />
        )}
      </div>

    );
  }
}

export default withRouter(WaferControlMaps);
